import React from 'react';
import { format, formatRelative } from 'date-fns';
import { useGate } from 'statsig-react';
import { gql } from '@soundxyz/gql-string';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { GetNotifiedButton } from '@components/EmailNotifications/GetNotifiedButton';
import { SongCard } from '@components/basics/SongCard';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment, UpcomingDropCardFragmentDoc } from '@graphql/reactQuery';
import { setUpNextTrackIds } from '@store/audio/AudioQueue';
import { FEATURE_GATES, SPACE } from '@utils/constants';
import { SpotifyPresaveButton } from '../SpotifyPresave/SpotifyPresaveButton';
import { ReleaseCardActions } from './ReleaseCardActions';
import { getCountdownString } from './ReleaseInfoCard';
import { useReleaseCardInfo } from './useReleaseCardInfo';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment UpcomingDropCard on Release {
    id
    supplyCutoffTimestamp
    mintStartTimestamp
    coverImage {
      id
      url
    }
    listeningParty
    spotifyPresaveCampaign
    ...ReleaseCardActions
    ...ReleasesCardInfo
  }
`);

const todayOrTomorrowRegex = /\b(today|tomorrow)\b/;

const EndingSaleCountdown = ({ endTimeMs }: { endTimeMs: number }) => {
  if (endTimeMs === 0) return null;
  return (
    <div className="ml-auto mr-2 mt-3 flex w-fit items-center overflow-hidden rounded-full bg-black/30 px-2 py-1 text-[10px] font-semibold leading-[14px] text-white backdrop-blur-lg sm:text-base-s">
      <span>Ends in{SPACE}</span>
      <span>{getCountdownString({ timeStamp: endTimeMs })}</span>
    </div>
  );
};

export const MintStartCountdown = ({ mintStartTimestamp }: { mintStartTimestamp: number }) => {
  if (mintStartTimestamp === 0) return null;
  return (
    <div className="ml-auto mr-2 mt-3 flex w-fit items-center overflow-hidden rounded-full bg-black/30 px-2 py-1 text-[10px] font-semibold leading-[14px] text-white backdrop-blur-lg sm:text-base-s">
      <span>
        Starts{SPACE}
        {todayOrTomorrowRegex.test(formatRelative(new Date(mintStartTimestamp), new Date()))
          ? formatRelative(new Date(mintStartTimestamp), new Date())?.split(' ')?.[0]
          : format(new Date(mintStartTimestamp), 'MMM d')}
        ,{SPACE}
        {/* Use date-fns to format time using AM/PM */}
        {format(new Date(mintStartTimestamp), 'h:mma')}
      </span>
    </div>
  );
};

export const UpcomingDropCard = ({
  data,
  getNextTrackIds,
}: {
  data: FragmentType<UpcomingDropCardFragmentDoc>;
  getNextTrackIds: () => string[];
}) => {
  const release = getFragment(UpcomingDropCardFragmentDoc, data);

  const {
    coverImageUrl,
    trackUrl,
    onPlayClick,
    isAudioBuffering: isBufferingAudio,
    playButtonPrefetchRef,
    isActiveTrack,
    isAlbum,
    hasAudioAccess,
    artistUserId,
    artistId,
    artistName,
    artistWebappPath,
    releaseTitle,
    currentListeningParty,
  } = useReleaseCardInfo({ data: release });

  const { value: isSpotifyPresaveEnabled } = useGate(FEATURE_GATES.SPOTIFY_PRESAVE);

  const { mintStartTimestamp, supplyCutoffTimestamp } = release;

  const now = Date.now();
  const hasSaleStarted = now > mintStartTimestamp;

  const header = hasSaleStarted ? (
    <EndingSaleCountdown endTimeMs={supplyCutoffTimestamp ?? 0} />
  ) : (
    <MintStartCountdown mintStartTimestamp={mintStartTimestamp ?? 0} />
  );
  const onPlayButtonClick = useStableCallback(async () => {
    await onPlayClick();
    setUpNextTrackIds(getNextTrackIds());
  });

  return (
    <SongCard
      album={isAlbum}
      isPlaying={isActiveTrack}
      loading={false}
      edition={false}
      expanded={null}
      setExpanded={null}
    >
      <SongCard.Cover
        header={header}
        coverLink={trackUrl}
        image={<SongCard.Cover.Single src={coverImageUrl} />}
        onPlayButtonClick={onPlayButtonClick}
        isBufferingSong={isBufferingAudio}
        disablePlay={!hasAudioAccess}
        playButtonPrefetchRef={playButtonPrefetchRef}
        currentListeningParty={currentListeningParty}
      />
      <SongCard.Info
        header={
          <AccessibleLink absoluteUri={trackUrl} type="external">
            {releaseTitle}
          </AccessibleLink>
        }
        subheader={<AccessibleLink pathnameUri={artistWebappPath}>{artistName}</AccessibleLink>}
        actions={<ReleaseCardActions data={release} />}
        bottom={
          <>
            {release.spotifyPresaveCampaign && isSpotifyPresaveEnabled ? (
              <SpotifyPresaveButton releaseId={release.id} variant="default" />
            ) : (
              <GetNotifiedButton type="upcoming_drop" userId={artistUserId} artistId={artistId} />
            )}
          </>
        }
      />
    </SongCard>
  );
};

export const UpcomingDropSkeletonCard = () => {
  return (
    <SongCard
      isPlaying={false}
      album={false}
      edition={false}
      expanded={null}
      setExpanded={null}
      loading
    >
      <SongCard.Cover
        image={null}
        onPlayButtonClick={() => {
          return;
        }}
        currentListeningParty={null}
      />
      <SongCard.Info
        header={null}
        subheader={null}
        actions={null}
        bottom={<SongCard.Info.Button variant="fuchsia" label="View Song" href="" />}
      />
    </SongCard>
  );
};
